import {
  Plus,
  Brain,
  MoreHorizontal,
  Pencil,
  Archive,
  CheckCircle2,
  Tag,
  ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import { TagGroup, TagOption, TaxonomyStatus } from "@/lib/schema";

import { Card, CardContent } from "@/components/ui/card";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

const STATUS_CONFIG: Record<
  TaxonomyStatus,
  {
    label: string;
    variant: "default" | "secondary" | "outline" | "destructive";
  }
> = {
  active: { label: "Active", variant: "default" },
  archived: { label: "Archived", variant: "secondary" },
};

export interface CustomGroupCardProps {
  group: TagGroup;
  options: Array<TagOption>;
  onEditGroup: (group: TagGroup) => void;
  onEditOption: (opt: TagOption) => void;
  onCreateOption: () => void;
  onArchiveGroup: (id: string) => void;
  onRestoreGroup: (id: string) => void;
  onArchiveOption: (id: string) => void;
  onRestoreOption: (id: string) => void;
  isAdmin: boolean;
}

export function CustomGroupCard({
  group,
  options,
  onEditGroup,
  onEditOption,
  onCreateOption,
  onArchiveGroup,
  onRestoreGroup,
  onArchiveOption,
  onRestoreOption,
  isAdmin,
}: CustomGroupCardProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Card data-testid={`group-${group.id}`}>
      <CardContent className="p-4 space-y-1">
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <div className="flex items-center gap-3">
            <CollapsibleTrigger asChild>
              <button
                className="flex items-center gap-3 flex-1 min-w-0 text-left cursor-pointer hover:opacity-80 transition-opacity"
                data-testid={`trigger-group-${group.id}`}
              >
                <Brain className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="font-medium text-foreground"
                      data-testid={`text-group-name-${group.id}`}
                    >
                      {group.name}
                    </span>
                    <Badge
                      variant="outline"
                      className="text-[10px] no-default-hover-elevate no-default-active-elevate"
                    >
                      {options.length} options
                    </Badge>
                  </div>
                  {group.description && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {group.description}
                    </p>
                  )}
                </div>
                <ChevronDown
                  className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
                />
              </button>
            </CollapsibleTrigger>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  data-testid={`button-group-actions-${group.id}`}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => onEditGroup(group)}
                  data-testid={`action-edit-group-${group.id}`}
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit Group
                </DropdownMenuItem>
                {group.status === "active" && isAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => onArchiveGroup(group.id)}
                      className="text-destructive"
                      data-testid={`action-archive-group-${group.id}`}
                    >
                      <Archive className="h-4 w-4 mr-2" />
                      Archive
                    </DropdownMenuItem>
                  </>
                )}
                {group.status === "archived" && isAdmin && (
                  <DropdownMenuItem
                    onClick={() => onRestoreGroup(group.id)}
                    data-testid={`action-restore-group-${group.id}`}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Restore
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <CollapsibleContent>
            <div className="space-y-2 mt-3">
              {options.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  No options yet. Add options to this group.
                </p>
              ) : (
                options.map((opt) => {
                  const optStatus = STATUS_CONFIG[opt.status];
                  return (
                    <div
                      key={opt.id}
                      className="flex items-center gap-3 p-3 rounded-md border border-border"
                      data-testid={`option-${opt.id}`}
                    >
                      <Tag className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p
                          className="text-sm font-medium text-foreground"
                          data-testid={`text-option-label-${opt.id}`}
                        >
                          {opt.label}
                        </p>
                        {opt.description && (
                          <p className="text-xs text-muted-foreground">
                            {opt.description}
                          </p>
                        )}
                      </div>
                      <Badge
                        variant={optStatus.variant}
                        className="text-[10px] no-default-hover-elevate no-default-active-elevate"
                      >
                        {optStatus.label}
                      </Badge>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`button-option-actions-${opt.id}`}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onEditOption(opt)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          {opt.status === "active" && isAdmin && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => onArchiveOption(opt.id)}
                                className="text-destructive"
                              >
                                <Archive className="h-4 w-4 mr-2" />
                                Archive
                              </DropdownMenuItem>
                            </>
                          )}
                          {opt.status === "archived" && isAdmin && (
                            <DropdownMenuItem
                              onClick={() => onRestoreOption(opt.id)}
                            >
                              <CheckCircle2 className="h-4 w-4 mr-2" />
                              Restore
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  );
                })
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={onCreateOption}
                className="mt-2"
                data-testid={`button-add-option-${group.id}`}
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Add Option
              </Button>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
}
